import Papa, { ParseResult } from "papaparse";
import React from "react";

import ApiService from "../../services/ApiService";
import { normalizeDate } from "../../services/TimeService";
import translate from "../../services/Translate";
import { detectCsvDelimiter } from "../../services/UtilityService";
import { InvitationImportRow } from "./Invitations.d";

const REQUIRED_COLUMNS = [
  "first_name",
  "last_name",
  "email",
  "language",
  "country",
  "dt_start",
  "dt_end",
  "cohort_ids",
  "is_vip",
  "has_extra_time",
  "invite_to_forum",
];

export const parseCsvFile = (
  file: File,
  setCsvData: React.Dispatch<React.SetStateAction<InvitationImportRow[]>>,
  language: string
) => {
  detectCsvDelimiter(file).then((delimiter) => {
    Papa.parse<InvitationImportRow>(file, {
      header: true,
      skipEmptyLines: true,
      delimiter: delimiter,
      complete: (results: ParseResult<InvitationImportRow>) => {
        if (results.errors.length > 0) {
          alert(translate(language, "PAGES.INVITATIONS.ERRORS.CSV_IMPORT"));
          return;
        }
        if (!results.meta.fields || REQUIRED_COLUMNS.some((col) => !results.meta.fields!.includes(col))) {
          const missingColumns = REQUIRED_COLUMNS.filter((col) => !results.meta.fields!.includes(col));
          const errorMessage = `${translate(language, "PAGES.INVITATIONS.ERRORS.MISSING_COLUMNS")}\n\n${translate(
            language,
            "PAGES.INVITATIONS.ERRORS.REQUIRED_COLUMNS_LIST"
          )}: ${REQUIRED_COLUMNS.join(", ")}`;
          if (missingColumns.length > 0) {
            alert(
              `${errorMessage}\n\n${translate(
                language,
                "PAGES.INVITATIONS.ERRORS.MISSING_COLUMNS_LIST"
              )}: ${missingColumns.join(", ")}`
            );
          } else {
            alert(errorMessage);
          }
          return;
        }
        const normalizedData = results.data.map((row) => ({
          ...row,
          dt_start: normalizeDate(row.dt_start),
          dt_end: normalizeDate(row.dt_end),
        }));
        setCsvData(normalizedData);
      },
      error: (error: Error) => {
        alert(translate(language, "PAGES.INVITATIONS.ERRORS.CSV_PARSING"));
        console.error("CSV Parsing Error:", error);
      },
    });
  });
};

const _cleanDataBeforeSubmitting = (data: InvitationImportRow[]) =>
  data.map((row) => ({
    first_name: row.first_name,
    last_name: row.last_name,
    email: row.email,
    language: row.language,
    country: row.country,
    dt_start: row.dt_start,
    dt_end: row.dt_end,
    cohort_ids: row.cohort_ids
      ? row.cohort_ids
          .split(",")
          .map((id) => parseInt(id.trim()))
          .filter((id) => !isNaN(id))
      : [],
    is_vip: row.is_vip === "1",
    has_extra_time: row.has_extra_time === "1",
    invite_to_forum: row.invite_to_forum === "1",
  }));

const _renderMissingCohortsSection = (successWithMissingCohorts: any[], language: string) => {
  if (!successWithMissingCohorts.length) return null;

  return (
    <React.Fragment>
      <p className="small mb-1">
        {translate(language, "PAGES.INVITATIONS.ALERTS.STATUS.SUCCESS_WITH_MISSING_COHORTS_EXPLANATION")}
      </p>
      <ul className="text-start small ps-3">
        {successWithMissingCohorts.map((item: any) => (
          <li key={`${item.row}-${item.email}`}>
            {item.email} — {translate(language, "PAGES.INVITATIONS.TABLE_HEADER.COHORT_IDS")}:{" "}
            {Array.isArray(item.missing_cohort_ids)
              ? item.missing_cohort_ids.join(", ")
              : String(item.missing_cohort_ids)}
          </li>
        ))}
      </ul>
    </React.Fragment>
  );
};

export const submitInvitationsImport = (
  csvData: InvitationImportRow[],
  setSubmitting: Function,
  fileInputRef: React.RefObject<HTMLInputElement>,
  setCsvData: Function,
  language: string,
  showAlert?: Function,
  popAlert?: Function
) => {
  if (csvData.length === 0) return;
  setSubmitting(true);

  showAlert?.({
    type: "info",
    title: translate(language, "PAGES.INVITATIONS.ALERTS.TITLES.IMPORTING"),
    message: translate(language, "PAGES.INVITATIONS.ALERTS.MESSAGES.IMPORTING"),
    clickOutsideToClose: false,
    buttons: [],
  });

  ApiService.post("users/invite/batch", {
    users: _cleanDataBeforeSubmitting(csvData),
  })
    .then((response) => {
      popAlert?.();
      const { success, errors } = response.data.message;

      const duplicateUsers =
        errors?.filter((error: any) => error.error && error.error.includes("already exists")) || [];
      const otherErrors = errors?.filter((error: any) => !error.error || !error.error.includes("already exists")) || [];
      const successWithMissingCohorts =
        success?.filter((item: any) => Array.isArray(item.missing_cohort_ids) && item.missing_cohort_ids.length > 0) ||
        [];

      if (success && success.length > 0) {
        if (errors && errors.length > 0) {
          showAlert?.({
            type: "warning",
            title: translate(language, "PAGES.INVITATIONS.ALERTS.TITLES.PARTIAL_SUCCESS"),
            message: translate(language, "PAGES.INVITATIONS.ALERTS.MESSAGES.PARTIAL_SUCCESS")
              .replace("{{CREATED}}", String(success.length))
              .replace("{{SKIPPED}}", String(duplicateUsers.length))
              .replace("{{ERRORS}}", String(otherErrors.length)),
            clickOutsideToClose: true,
            buttons: [
              {
                label: translate(language, "PAGES.INVITATIONS.ALERTS.BUTTONS.OK"),
                onClick: () => popAlert?.(),
              },
            ],
            extraContent: (
              <div className="mt-3">
                <h6>
                  {translate(language, "PAGES.INVITATIONS.ALERTS.STATUS.SUCCESSFULLY_CREATED").replace(
                    "{{COUNT}}",
                    String(success.length)
                  )}
                </h6>
                {_renderMissingCohortsSection(successWithMissingCohorts, language)}
                {duplicateUsers.length > 0 && (
                  <React.Fragment>
                    <h6 className="mt-3">
                      {translate(language, "PAGES.INVITATIONS.ALERTS.STATUS.SKIPPED_ALREADY_EXISTS").replace(
                        "{{COUNT}}",
                        String(duplicateUsers.length)
                      )}
                    </h6>
                    <pre
                      className="text-start off-white-background p-2 rounded border small overflow-auto"
                      style={{ maxHeight: 150 }}
                    >
                      {JSON.stringify(duplicateUsers, null, 2)}
                    </pre>
                  </React.Fragment>
                )}
                {otherErrors.length > 0 && (
                  <React.Fragment>
                    <h6 className="mt-3">
                      {translate(language, "PAGES.INVITATIONS.ALERTS.STATUS.ERRORS").replace(
                        "{{COUNT}}",
                        String(otherErrors.length)
                      )}
                    </h6>
                    <pre
                      className="text-start off-white-background p-2 rounded border small overflow-auto"
                      style={{ maxHeight: 150 }}
                    >
                      {JSON.stringify(otherErrors, null, 2)}
                    </pre>
                  </React.Fragment>
                )}
              </div>
            ),
          });
        } else if (successWithMissingCohorts.length > 0) {
          showAlert?.({
            type: "warning",
            title: translate(language, "PAGES.INVITATIONS.ALERTS.TITLES.PARTIAL_SUCCESS"),
            message: translate(language, "PAGES.INVITATIONS.ALERTS.MESSAGES.PARTIAL_SUCCESS")
              .replace("{{CREATED}}", String(success.length))
              .replace("{{SKIPPED}}", "0")
              .replace("{{ERRORS}}", "0"),
            clickOutsideToClose: true,
            buttons: [
              {
                label: translate(language, "PAGES.INVITATIONS.ALERTS.BUTTONS.OK"),
                onClick: () => popAlert?.(),
              },
            ],
            extraContent: (
              <div className="mt-3">{_renderMissingCohortsSection(successWithMissingCohorts, language)}</div>
            ),
          });
        } else {
          showAlert?.({
            type: "success",
            title: translate(language, "PAGES.INVITATIONS.ALERTS.TITLES.SUCCESS"),
            message: translate(language, "PAGES.INVITATIONS.ALERTS.MESSAGES.SUCCESS"),
            clickOutsideToClose: true,
            buttons: [
              {
                label: translate(language, "PAGES.INVITATIONS.ALERTS.BUTTONS.OK"),
                onClick: () => popAlert?.(),
              },
            ],
          });
        }
      } else {
        if (duplicateUsers.length > 0 && otherErrors.length === 0) {
          showAlert?.({
            type: "info",
            title: translate(language, "PAGES.INVITATIONS.ALERTS.TITLES.WERE_SKIPPED"),
            message: translate(language, "PAGES.INVITATIONS.ALERTS.MESSAGES.WERE_SKIPPED").replace(
              "{{COUNT}}",
              String(duplicateUsers.length)
            ),
            clickOutsideToClose: true,
            buttons: [
              {
                label: translate(language, "PAGES.INVITATIONS.ALERTS.BUTTONS.OK"),
                onClick: () => popAlert?.(),
              },
            ],
            extraContent: (
              <div className="mt-3">
                <h6>
                  {translate(language, "PAGES.INVITATIONS.ALERTS.STATUS.SKIPPED_ALREADY_EXISTS").replace(
                    "{{COUNT}}",
                    String(duplicateUsers.length)
                  )}
                </h6>
                <pre
                  className="text-start off-white-background p-2 rounded border small overflow-auto"
                  style={{ maxHeight: 200 }}
                >
                  {JSON.stringify(duplicateUsers, null, 2)}
                </pre>
              </div>
            ),
          });
        } else {
          showAlert?.({
            type: "danger",
            title: translate(language, "PAGES.INVITATIONS.ALERTS.TITLES.PROCESSING_FAILED"),
            message: translate(language, "PAGES.INVITATIONS.ALERTS.MESSAGES.PROCESSING_FAILED")
              .replace("{{SKIPPED}}", String(duplicateUsers.length))
              .replace("{{ERRORS}}", String(otherErrors.length)),
            clickOutsideToClose: true,
            buttons: [
              {
                label: translate(language, "PAGES.INVITATIONS.ALERTS.BUTTONS.CLOSE"),
                variant: "danger",
                onClick: () => popAlert?.(),
              },
            ],
            extraContent: (
              <div className="mt-3">
                {duplicateUsers.length > 0 && (
                  <React.Fragment>
                    <h6>
                      {translate(language, "PAGES.INVITATIONS.ALERTS.STATUS.SKIPPED_ALREADY_EXISTS").replace(
                        "{{COUNT}}",
                        String(duplicateUsers.length)
                      )}
                    </h6>
                    <pre
                      className="text-start off-white-background p-2 rounded border small overflow-auto"
                      style={{ maxHeight: 150 }}
                    >
                      {JSON.stringify(duplicateUsers, null, 2)}
                    </pre>
                  </React.Fragment>
                )}
                {otherErrors.length > 0 && (
                  <React.Fragment>
                    <h6 className="mt-3">
                      {translate(language, "PAGES.INVITATIONS.ALERTS.STATUS.ERRORS").replace(
                        "{{COUNT}}",
                        String(otherErrors.length)
                      )}
                    </h6>
                    <pre
                      className="text-start off-white-background p-2 rounded border small overflow-auto"
                      style={{ maxHeight: 150 }}
                    >
                      {JSON.stringify(otherErrors, null, 2)}
                    </pre>
                  </React.Fragment>
                )}
              </div>
            ),
          });
        }
      }

      setCsvData([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    })
    .catch((err: any) => {
      popAlert?.();
      showAlert?.({
        type: "danger",
        title: translate(language, "PAGES.INVITATIONS.ALERTS.TITLES.ERROR"),
        message: err?.data?.message || translate(language, "PAGES.INVITATIONS.ALERTS.MESSAGES.ERROR"),
        clickOutsideToClose: true,
        buttons: [
          {
            label: translate(language, "PAGES.INVITATIONS.ALERTS.BUTTONS.CLOSE"),
            variant: "danger",
            onClick: () => popAlert?.(),
          },
        ],
        extraContent: err?.data ? (
          <pre className="text-start off-white-background p-2 rounded border">{JSON.stringify(err.data, null, 2)}</pre>
        ) : null,
      });
    })
    .finally(() => {
      setSubmitting(false);
    });
};
